import React, { useCallback } from 'react';
import { toast } from 'react-toastify';
import { isEmpty } from 'lodash';
import ToastComponent from '../../components/toast';
import { handleOpenFile } from '../../utils/global';

// Types
export interface ImportExportConfig {
  downloadService?: any;
  importService?: any;
  fileName?: string;
  successMessage?: string;
  payloadKey?: string;
  downloadMode?: 'file' | 'toast'; // New option to control download behavior
  downloadSuccessMessage?: string;
  acceptedFileTypes?: string; // File types accepted for import (e.g., '.json', '.xlsx, .xls')
}

export interface UseImportExportProps {
  handleRequest: (service: any) => Promise<any>;
  config: ImportExportConfig;
}

export interface ImportExportHandlers {
  downloadTemplate: () => void;
  importData: (event: any) => void;
}

/**
 * Custom hook for handling import/export operations
 * Provides standardized download and import functionality
 */
export const useImportExport = ({
  handleRequest,
  config,
}: UseImportExportProps): ImportExportHandlers => {
  const {
    downloadService,
    importService,
    fileName = 'Template.xlsx',
    successMessage = 'Import job submitted successfully.',
    payloadKey = 'File',
    downloadMode = 'file',
    downloadSuccessMessage = 'Export completed successfully.',
  } = config;

  const downloadTemplate = useCallback((): void => {
    if (!downloadService) {
      console.warn('Download service not provided');
      return;
    }

    // Call the service function to get the actual service call
    const serviceCall = typeof downloadService === 'function' ? downloadService() : downloadService;

    handleRequest(serviceCall).then((data: any) => {
      if (downloadMode === 'toast') {
        // For toast mode, just show success message
        toast.success(
          React.createElement(ToastComponent, {
            label: 'Success',
            message: downloadSuccessMessage,
          })
        );
      } else {
        // Default file download mode
        if (data) {
          const content = data.content || data.data || data;
          const name = data.name ? `${data.name}.xlsx` : fileName;
          handleOpenFile(name, content);
        }
      }
    });
  }, [handleRequest, downloadService, fileName, downloadMode, downloadSuccessMessage]);

  const importData = useCallback(
    (event: any): void => {
      if (!importService) {
        console.warn('Import service not provided');
        return;
      }

      if (!isEmpty(event.target.files)) {
        const file = event.target.files[0];

        // Validate file type if acceptedFileTypes is specified
        if (config.acceptedFileTypes) {
          const allowedExtensions = config.acceptedFileTypes.split(',').map((ext) => ext.trim());
          const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

          if (!allowedExtensions.includes(fileExtension)) {
            const fileTypeNames = allowedExtensions
              .map((ext) => ext.replace('.', '').toUpperCase())
              .join(' or ');
            toast.error(
              React.createElement(ToastComponent, {
                label: 'Error',
                message: `Invalid file type. Please upload ${fileTypeNames} files only.`,
              })
            );
            event.target.value = null;
            return;
          }
        }

        const payload = { [payloadKey]: file };

        handleRequest(importService(payload))
          .then((data: any) => {
            if (data) {
              toast.success(
                React.createElement(ToastComponent, { label: 'Success', message: successMessage })
              );
            }
          })
          .catch((error) => {
            console.error('Import failed:', error);
          })
          .finally(() => {
            event.target.value = null;
          });
      }
    },
    [handleRequest, importService, successMessage, payloadKey, config.acceptedFileTypes]
  );

  return {
    downloadTemplate,
    importData,
  };
};

/**
 * Hook for handling mutation-based imports (like notifications, award agreements)
 */
export const useMutationImport = (
  mutationFn: (payload: any) => void,
  payloadKey: string = 'File',
  acceptedFileTypes?: string
) => {
  return useCallback(
    (event: any): void => {
      if (!isEmpty(event.target.files)) {
        const file = event.target.files[0];

        // Validate file type if acceptedFileTypes is specified
        if (acceptedFileTypes) {
          const allowedExtensions = acceptedFileTypes.split(',').map((ext) => ext.trim());
          const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

          if (!allowedExtensions.includes(fileExtension)) {
            const fileTypeNames = allowedExtensions
              .map((ext) => ext.replace('.', '').toUpperCase())
              .join(' or ');
            toast.error(
              React.createElement(ToastComponent, {
                label: 'Error',
                message: `Invalid file type. Please upload ${fileTypeNames} files only.`,
              })
            );
            event.target.value = null;
            return;
          }
        }

        const payload = { [payloadKey]: file };
        mutationFn(payload);
        event.target.value = null;
      }
    },
    [mutationFn, payloadKey, acceptedFileTypes]
  );
};

/**
 * Hook for handling info toast messages (for features requiring additional context)
 */
export const useInfoHandler = (message: string) => {
  return useCallback((): void => {
    toast.info(React.createElement(ToastComponent, { label: 'Info', message }));
  }, [message]);
};
